import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzTableModule, NzTableSortFn, NzTableSortOrder } from 'ng-zorro-antd/table';
import { AngularModule } from '../../../../../modules/angular.module';
import { Section } from '@mira-avant/mtlibs';
import { SectionService } from '../../../../../services/section.service';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { LocationNamePipe } from '../../../../../pipes/location-name-pipe';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';

@Component({
	selector: 'app-sections-table',
	standalone: true,
	imports: [
		AngularModule,
		NzTableModule,
		NzEmptyModule,
		NzPaginationModule,
		LocationNamePipe,
		NzPopconfirmModule,
		NzButtonModule,
		NzIconModule,
	],
	templateUrl: './sections-table.html',
	styleUrl: './sections-table.scss',
})
export class SectionsTable {
	@Input('clientUuid') public clientUuid: string | undefined;
	@Output('onSectionSelected') public onSectionSelected: EventEmitter<Section | null> =
		new EventEmitter<Section | null>();

	public sections = signal<Section[]>([]);
	public selectedSection = signal<Section | null>(null);
	public sortOrder = signal<NzTableSortOrder>(null);
	public totalSections = signal<number>(0);
	public pageNumber = signal<number>(1);
	public SECTIONS_PER_PAGE = 2;
	public loading = signal<boolean>(false);

	constructor(private sectionService: SectionService) {}

	async ngOnInit(): Promise<void> {
		await this.reloadPage();
	}

	public async loadPageOfSections(pageNumber: number): Promise<void> {
		this.loading.set(true);
		this.pageNumber.set(pageNumber);

		try {
			const sections = await this.sectionService.getSectionsByClient$(
				this.clientUuid || '...missing...',
				pageNumber - 1,
				this.SECTIONS_PER_PAGE
			);

			this.sections.set(sections);
		} catch (error) {
			console.error('Error loading sections:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public selectSection(section: Section | null): void {
		this.selectedSection.set(section);
		this.onSectionSelected.emit(section);
	}

	public async reloadPage(): Promise<void> {
		await this.loadPageOfSections(this.pageNumber());
		await this.updateTotals();
	}

	public async reset(): Promise<void> {
		this.pageNumber.set(1);
		await this.loadPageOfSections(this.pageNumber());
		await this.updateTotals();
	}

	public async updateTotals(): Promise<void> {
		if (!this.clientUuid) {
			throw new Error('No client selected');
		}

		await this.sectionService.getTotalSectionsByClient(this.clientUuid).then((totalSections: number) => {
			this.totalSections.set(totalSections);
		});
	}

	public async onPageIndexChange(pageNumber: number): Promise<void> {
		await this.loadPageOfSections(pageNumber);
	}

	public async reloadCurrentPage(): Promise<void> {
		await this.loadPageOfSections(this.pageNumber());
		await this.updateTotals();
	}

	public sortNameFn: NzTableSortFn<Section> = (a, b) => {
		const nameA = a.name?.toLowerCase() || '';
		const nameB = b.name?.toLowerCase() || '';
		return nameA.localeCompare(nameB);
	};

	public onSortOrderChange(order: NzTableSortOrder): void {
		this.sortOrder.set(order);
	}
}
